import { Tab } from '@headlessui/react'
import classNames from 'classnames'
import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { ReleaseCalendarBanner } from 'components/release-calendar/Banner'
import { AllTab } from 'components/release-calendar/tabs/AllTab'
import { PastTab } from 'components/release-calendar/tabs/PastTab'
import { SavedTab } from 'components/release-calendar/tabs/SavedTab'
import routes from 'constants/routes'
import { useAuth, useAuthModal, useOverlay } from 'contexts'
import { useUserProfile } from 'hooks'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import {
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from 'react'
const CalendarSettingsModal = dynamic(
  () =>
    import('components/release-calendar/CalendarSettingsModal').then(
      m => m.CalendarSettingsModal
    ),
  { ssr: false }
)
const SubmitReleaseModal = dynamic(
  () =>
    import('components/release-calendar/SubmitReleaseModal').then(
      m => m.SubmitReleaseModal
    ),
  { ssr: false }
)

const NeverMissAReleaseAgain = dynamic(
  () =>
    import('components/release-calendar/NeverMissARelease').then(
      m => m.NeverMissAReleaseAgain
    ),
  { ssr: false }
)

const tabs = ['all', 'saved', 'past']

export const ReleaseCalendar = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation('release_calendar')
  const { setIsOpen, setConfig } = useOverlay()
  const { data: userProfile } = useUserProfile()
  const { user, isLoading: isLoadingAuth } = useAuth()
  const { setState: setAuthModalState, setIsOpen: setIsOpenAuthModal } =
    useAuthModal()
  const router = useRouter()

  const currentTab = (() => {
    const queryTab = router.query.tab
    if (typeof queryTab !== 'string' || !tabs.includes(queryTab.toLowerCase()))
      return 0
    return tabs.indexOf(queryTab.toLowerCase())
  })()

  const headerRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!userProfile) return

    if (!userProfile?.releaseSettings?.timeZone) {
      setConfig({
        className: 'sm:flex items-center px-4',
        body: <CalendarSettingsModal />,
      })
      setIsOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile])

  const onTabChange = useCallback(
    async (tabIndex: number) => {
      if (!user) {
        setAuthModalState('register')
        setIsOpenAuthModal(true)
        return
      }

      router.push(
        {
          pathname: routes.releaseCalendar.base,
          query: { tab: tabs[tabIndex] },
        },
        undefined,
        { scroll: false }
      )

      if (!headerRef.current || !contentRef.current) return

      const headerRect = headerRef.current.getBoundingClientRect()

      if (headerRect.y > 64) return

      const contentRect = contentRef.current.getBoundingClientRect()

      const top = contentRect.top + window.scrollY - headerRect.height - 64

      window.scrollTo({ top })
    },
    [user, headerRef, contentRef, router]
  )

  const onSubmitRelease = () => {
    if (!user) {
      setAuthModalState('register')
      setIsOpenAuthModal(true)
      return
    }

    setConfig({
      className: 'sm:flex sm:items-center sm:px-4 items-end',
      bodyClassName: 'h-full items-end sm:items-center',
      body: <SubmitReleaseModal />,
    })
    setIsOpen(true)
  }

  const onUpdateSettings = () => {
    if (!user) {
      setAuthModalState('register')
      setIsOpenAuthModal(true)
      return
    }

    setConfig({
      className: 'sm:flex items-center px-4',
      body: <CalendarSettingsModal />,
    })
    setIsOpen(true)
  }

  return (
    <div className='h-full flex-1'>
      <ReleaseCalendarBanner />

      <Tab.Group selectedIndex={currentTab} onChange={onTabChange}>
        {!user && (
          <div className='bg-warm-50 px-6 pb-8 sm:hidden'>
            <Button
              style='solid-primary'
              className='w-full'
              size='md'
              pill
              onClick={() => {
                setAuthModalState('register')
                setIsOpenAuthModal(true)
              }}
            >
              {t('common:sign_up')}
            </Button>
          </div>
        )}

        <div className='z-10 -mt-6 contents w-full sm:sticky sm:top-16 sm:mt-0 sm:block sm:bg-cool-50 sm:px-6 sm:pb-8 sm:pt-8 md:px-12 lg:top-20'>
          <div className='mx-auto contents w-full max-w-[90rem] justify-between sm:flex sm:flex-row-reverse'>
            <div
              className={classNames(
                'items-center justify-between space-x-2.5 whitespace-nowrap bg-warm-50 px-6 pb-8 sm:bg-transparent sm:p-0 sm:flex',
                user ? 'flex' : 'hidden'
              )}
            >
              <Button
                style='solid-primary'
                pill
                className='w-full sm:h-12 sm:text-base'
                onClick={onSubmitRelease}
                data-e2e='submit-release-btn'
              >
                {t('submit_release')}
              </Button>

              <Button
                style='solid-white'
                pill
                className={
                  '!border-warm-100 sm:h-12 sm:w-12 sm:max-w-[2.75rem] sm:!bg-warm-100 [@media(max-width:370px)]:max-w-[2.5rem] [@media(min-width:369px)]:w-full'
                }
                onClick={onUpdateSettings}
                data-e2e='settings-btn'
              >
                <span className='mr-2 sm:sr-only [@media(max-width:369px)]:sr-only'>
                  {t('settings')}
                </span>

                <Icon name='settings' />
              </Button>
            </div>

            <div
              ref={headerRef}
              className='sticky top-16 z-10 -mb-4 w-full bg-cool-50 px-6 py-4 sm:m-0 sm:p-0 sm:pt-0'
            >
              <Tab.List className='grid max-h-[3rem] min-h-[3rem] w-full grid-cols-3 items-center rounded-full bg-warm-100 p-1 sm:max-h-[3.5rem] sm:min-h-[3.5rem] sm:max-w-[20.4375rem]'>
                {tabs.map((tab, index) => (
                  <Tab key={tab} as={Fragment}>
                    {({ selected }) => (
                      <button
                        className={classNames(
                          'flex items-center justify-center border-cool-400 px-4 text-sm font-medium outline-none transition duration-200 ease-in-out sm:px-5 sm:text-base',
                          selected
                            ? 'h-full rounded-full bg-cool-50 py-2 '
                            : 'max-h-[1.25rem] text-cool-700 sm:max-h-[1.75rem]',
                          index === 0 && currentTab === 2 && 'border-r',
                          index === 2 && currentTab === 0 && 'border-l'
                        )}
                        data-e2e={`tab-${tab}-btn`}
                      >
                        <span className={index === 1 ? 'sr-only' : undefined}>
                          {t(tab)}
                        </span>

                        {index === 1 && (
                          <Icon
                            name='filled-bell'
                            filled
                            className={
                              selected ? 'text-cool-900' : 'text-cool-700'
                            }
                          />
                        )}
                      </button>
                    )}
                  </Tab>
                ))}
              </Tab.List>
            </div>
          </div>
        </div>

        <Tab.Panels ref={contentRef} className='relative'>
          <Tab.Panel>
            <AllTab />
          </Tab.Panel>

          <Tab.Panel>
            <SavedTab />
          </Tab.Panel>

          <Tab.Panel>
            <PastTab />
          </Tab.Panel>

          {!user && !isLoadingAuth && (
            <div className='pointer-events-none absolute bottom-0 left-0 z-[10] h-80 w-full bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFF_100%)] sm:h-[22rem] md:h-96'></div>
          )}
        </Tab.Panels>
      </Tab.Group>

      {!user && !isLoadingAuth && <NeverMissAReleaseAgain />}

      {children}
    </div>
  )
}
